/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-mixins, warp-drive/no-legacy-request-patterns */
// Extends From
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import EditPostMixin from '@linc-technologies/linced-common/mixins/edit-post';

// Internal Imports
import PostTypes from '@linc-technologies/linced-common/enums/post-type';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { getGroupVisibilityFilter } from 'linced-app/utils/groups';

// Ember Imports
import { action, computed } from '@ember/object';
import { setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

export default class IndexController extends Controller.extend(EditPostMixin) {
  // Constants
  queryParams = [
    'authorIds',
    'autoFilter',
    'endTime',
    'groupIds',
    'offset',
    'personIds',
    'searchQuery',
    'startTime',
    'status',
  ];

  PostTypes = PostTypes;

  // Services
  @service
  session;

  @service
  store;

  @service
  students;

  @service
  toaster;

  // State
  @tracked
  authorIds = [];

  @tracked
  autoFilter = '';
  @tracked
  endTime = '';
  @tracked
  groupIds = [];
  @tracked offset = 0;
  @tracked
  personIds = [];
  @tracked searchQuery = '';
  @tracked
  startTime = '';
  @tracked status = 0;

  // Dynamic
  @computed('authorIds.[],model.authors.[]')
  get selectedAuthors() {
    return this.authorIds.map((id) => {
      return this.store.peekRecord('person', id);
    });
  }

  @setDiff('model.authors', 'selectedAuthors')
  remainingAuthors;

  @computed('remainingAuthors')
  get sortedRemainingAuthors() {
    return this.remainingAuthors
      .slice()
      .sort(
        (a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '') ||
          (a.firstName || '').localeCompare(b.firstName || ''),
      );
  }

  @computed('groupIds.[],model.groups.[]')
  get selectedGroups() {
    return this.groupIds.map((id) => {
      return this.store.peekRecord('group', id);
    });
  }

  @setDiff('model.groups', 'selectedGroups')
  remainingGroups;

  @computed('remainingGroups.[]', 'session.personId')
  get sortedFilteredRemainingGroups() {
    return this.remainingGroups
      .filter(getGroupVisibilityFilter(this.session.personId))
      .sort(byKeys('name'));
  }

  @computed('personIds.[]', 'students.{all.[],resolved.[]}')
  get selectedStudents() {
    // Trigger students service to load all students if not already primed.
    // This property will then recalculate once they're loaded.
    this.students.all;
    if (isEmpty(this.students.resolved)) {
      return [];
    }
    return this.personIds.map((id) => {
      return this.store.peekRecord('person', id);
    });
  }

  @setDiff('students.resolved', 'selectedStudents')
  remainingStudents;

  @computed('remainingStudents.[]')
  get sortedRemainingStudents() {
    return this.remainingStudents
      .slice()
      .sort(
        (a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '') ||
          (a.firstName || '').localeCompare(b.firstName || ''),
      );
  }

  // Functions
  @action
  addAuthor(author) {
    if (this.authorIds.length >= 5) {
      this.toaster.error(
        'Filtering has a maximum of 5 authors. Please remove one before adding more.',
      );
    } else {
      this.authorIds = [...this.authorIds, author.get('id')];
    }
  }

  @action
  removeAuthor(author) {
    let id = author.get('id');
    this.authorIds = this.authorIds.filter((authorId) => authorId !== id);
  }

  @action
  addGroup(group) {
    if (this.groupIds.length >= 5) {
      this.toaster.error(
        'Filtering has a maximum of 5 groups. Please remove one before adding more.',
      );
    } else {
      this.groupIds = [...this.groupIds, group.id];
    }
  }

  @action
  removeGroup(group) {
    this.groupIds = this.groupIds.filter((id) => id !== group.id);
  }

  @action
  setStartTime(startTime) {
    this.startTime = startTime;
    this.autoFilter = '';
  }

  @action
  setEndTime(endTime) {
    this.endTime = endTime;
    this.autoFilter = '';
  }

  @action
  clear() {
    this.startTime = '';
    this.endTime = '';
    this.autoFilter = '';
    this.authorIds = [];
    this.personIds = [];
  }

  @action
  filterDay() {
    this.autoFilter !== 'day'
      ? (this.autoFilter = 'day')
      : (this.autoFilter = '');
    this.startTime = '';
    this.endTime = '';
  }

  @action
  filterWeek() {
    this.autoFilter !== 'week'
      ? (this.autoFilter = 'week')
      : (this.autoFilter = '');
    this.startTime = '';
    this.endTime = '';
  }

  @action
  filterSevenDays() {
    this.autoFilter !== '7-days'
      ? (this.autoFilter = '7-days')
      : (this.autoFilter = '');
    this.startTime = '';
    this.endTime = '';
  }

  @action
  filterPostStatus(newStatus) {
    this.status !== newStatus ? (this.status = newStatus) : (this.status = 0);
  }

  @action
  refresh() {
    this.model.update();
  }

  @action
  nextPage() {
    this.incrementProperty('offset', 20);
  }

  @action
  prevPage() {
    this.decrementProperty('offset', 20);
  }

  @action
  addStudent(i) {
    if (this.personIds.length >= 5) {
      this.toaster.error(
        'Filtering has a maximum of 5 students. Please remove one before adding more.',
      );
    } else {
      this.personIds = [...this.personIds, i.get('id')];
    }
  }

  @action
  removeStudent(i) {
    let id = i.get('id');
    this.personIds = this.personIds.filter((personId) => personId !== id);
  }

  @action
  setSearchQuery(value) {
    this.searchQuery = value;
    this.offset = 0;
  }
}
